import { Box } from '@mui/material';
import useDialogModal from '../../../hooks/useDialogModal';
import EnhancedTable from '../../enhancedTable/EnhancedTable';
import { useCallback, useEffect, useReducer } from 'react';
import { ValidRoleService } from '../../../service/validDataService';
import useAbortController from '../../../hooks/useAbortController';
import ConfirmationDialog from '../../ui/confirmationDialog';
import validLookup from '../../../utils/validLookup';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import { roleColumns } from './columns';
import AddRoleForm from './addRoleForm';
import EditRoleForm from './editRoleForm';

export default function ManageRole() {
  const [AddDialog, showAdd] = useDialogModal(AddRoleForm);
  const [EditDialog, showEdit] = useDialogModal(EditRoleForm);
  const [ConfirmDialog, showConfirm, closeConfirm] = useDialogModal(ConfirmationDialog);

  const [state, updateState] = useReducer((s, p) => ({ ...s, ...p }), {
    rows: [],
    totalRowCount: 0,
    pageCount: 0,
    loading: false,
  });

  const [signal, abort] = useAbortController();
  useEffect(() => () => abort(), [abort]);

  const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    ValidRoleService.getWithLimit(from, pageSize, filters, sortBy, signal)
      .then(({ code, data }) => {
        if (code === 0) {
          updateState({ rows: data.data, totalRowCount: data.count, pageCount: Math.ceil(data.count / pageSize) });
        }
        updateState({ loading: false });
      })
      .catch(() => updateState({ loading: false }));
  }, [signal]);

  const onAdd = () => showAdd();
  const onAdded = (newRows) => updateState({ rows: state.rows.concat(newRows) });

  const onEdit = (row) => showEdit(null, { role: row });
  const onEdited = (updated) => {
    const data = Array.isArray(updated) ? updated[0] : updated;
    updateState({ rows: state.rows.map(r => (r.id === data.id ? { ...r, ...data } : r)) });
  };

  const onDelete = (rowids) => {
    showConfirm(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        const { code, message } = await ValidRoleService.delete(rowids.map(x => parseInt(x, 10)));
        if (code === 0) {
          toast.success(message || 'Deleted');
          updateState({ rows: state.rows.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id)) });
        } else {
          toast.error('Failed to delete record(s)');
        }
      }
      closeConfirm();
    }, { title: 'Delete record', message: `Please confirm if you would like to delete ${rowids.length} record(s)?` });
  };

  return (
    <Box>
      <EnhancedTable
        columns={roleColumns}
        addRecordHandler={null}
        deleteRecordHandler={null}
        data={state.rows}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchData}
        loading={state.loading}
        // actions={[{ icon: <EditIcon />, onClick: onEdit }]}
      />
      <AddDialog onRecordAdded={onAdded} />
      <EditDialog onRecordEdited={onEdited} />
      <ConfirmDialog label={{ positive: 'Confirm', negative: 'Cancel' }} />
    </Box>
  );
}
